import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db } from '../../firebase/config';
import {
    collection,
    getDocs,
    addDoc,
    updateDoc,
    doc,
    serverTimestamp,
} from 'firebase/firestore';
import './AdminPromotionDetails.css';
import { isProductInStock } from '../../firebase/products';
import { uploadToFirebaseStorage } from '../../utils/firebaseStorage';

function toDateInput(ts) {
    if (!ts) return '';
    let d;
    if (typeof ts.toDate === 'function') { d = ts.toDate(); }
    else if (ts.seconds !== undefined)   { d = new Date(ts.seconds * 1000); }
    else if (ts instanceof Date)         { d = ts; }
    else if (typeof ts === 'string') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(ts)) return ts;
        d = new Date(ts);
    }
    else if (typeof ts === 'number') { d = new Date(ts); }
    else { return ''; }
    if (!d || isNaN(d.getTime())) return '';
    const year  = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day   = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function getTodayDateInput() {
    return toDateInput(new Date());
}

function getTomorrowDateInput() {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return toDateInput(d);
}

function StatusBadge({ status }) {
    const styles = {
        active:    { background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' },
        inactive:  { background: '#f5f5f5', color: '#777',    border: '1px solid #e0e0e0' },
        scheduled: { background: '#e3f0ff', color: '#1a5fa8', border: '1px solid #b3d1f5' },
    };
    const s = styles[status] || styles.inactive;
    return (
        <span style={{ ...s, display: 'inline-block', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
            {status ? status.charAt(0).toUpperCase() + status.slice(1) : '—'}
        </span>
    );
}

function normalizeText(value) {
    return typeof value === 'string' ? value.trim() : '';
}

export default function AdminPromotionDetails() {
    const location = useLocation();
    const navigate = useNavigate();
    const existing = location.state?.promotion || null;
    const isNew    = !existing;
    const fileRef  = useRef();

    const [status,             setStatus]             = useState(existing?.status             || 'active');
    const [promotionName,      setPromotionName]      = useState(existing?.promotionName      || '');
    const [promoCode,          setPromoCode]          = useState(existing?.promoCode          || '');
    const [description,        setDescription]        = useState(existing?.description        || '');
    const [discountedProducts, setDiscountedProducts] = useState(existing?.discountedProducts || '');
    const [promotionType,      setPromotionType]      = useState(existing?.promotionType      || 'percentage');
    const [discountValue,      setDiscountValue]      = useState(existing?.discountValue      || '');
    const [startDate,          setStartDate]          = useState(() => toDateInput(existing?.startDate));
    const [endDate,            setEndDate]            = useState(() => toDateInput(existing?.endDate));
    const [usageLimitType,     setUsageLimitType]     = useState(existing?.usageLimitType || 'unlimited');
    const [usageLimitValue,    setUsageLimitValue]    = useState(existing?.usageLimitValue ?? '');
    const [dropdownOpen,       setDropdownOpen]       = useState(false);
    const dropdownRef = useRef();
    const [expandedCats, setExpandedCats] = useState({});
    const [expandedSubs, setExpandedSubs] = useState({});
    const totalUsageCount = existing?.totalUsageCount ?? 0;

    const [imageFile,    setImageFile]    = useState(null);
    const [imagePreview, setImagePreview] = useState(existing?.imageUrl || '');
    const [imageUrl,     setImageUrl]     = useState(existing?.imageUrl || '');
    const [products,     setProducts]     = useState([]);
    const [categories,   setCategories]   = useState([]);
    const [saving,       setSaving]       = useState(false);
    const [msg,          setMsg]          = useState(null);

    useEffect(() => { fetchCatalogData(); }, []);

    // Align schedule with status instead of blocking save with errors
    useEffect(() => {
        const todayStr = getTodayDateInput();
        const tomorrowStr = getTomorrowDateInput();
        if (status === 'active') {
            setStartDate(todayStr);
            setEndDate(tomorrowStr);
        } else if (status === 'scheduled') {
            setStartDate((prev) => (!prev || prev < todayStr) ? todayStr : prev);
        }
    }, [status]);

    useEffect(() => {
        if (status === 'scheduled' && startDate) {
            const d = new Date(startDate + 'T00:00:00');
            d.setDate(d.getDate() + 1);
            const minEnd = toDateInput(d);
            setEndDate((prev) => (!prev || prev < minEnd) ? minEnd : prev);
        }
    }, [status, startDate]);

    useEffect(() => {
        function handleClickOutside(e) {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    async function fetchCatalogData() {
        try {
            const [productSnap, categorySnap] = await Promise.all([
                getDocs(collection(db, 'products')),
                getDocs(collection(db, 'categories')),
            ]);
            setProducts(productSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            setCategories(categorySnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) { console.error('Error fetching promotion selector data:', err); }
    }

    function handleImageChange(e) {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = ev => setImagePreview(ev.target.result);
        reader.readAsDataURL(file);
        setImageFile(file);
    }

    async function handleSave() {
        if (!promotionName.trim()) { setMsg({ type: 'error', text: 'Promotion name is required.' }); return; }
        if (!promoCode.trim()) { setMsg({ type: 'error', text: 'Promo code is required.' }); return; }
        if (!discountValue)        { setMsg({ type: 'error', text: 'Discount value is required.' }); return; }
        if (!startDate || !endDate){ setMsg({ type: 'error', text: 'Start and end dates are required.' }); return; }

        const todayStr = getTodayDateInput();
        let resolvedStartDate = startDate;
        let resolvedEndDate = endDate;

        if (status === 'active') {
            resolvedStartDate = todayStr;
        } else if (status === 'scheduled') {
            if (!resolvedStartDate || resolvedStartDate < todayStr) {
                resolvedStartDate = todayStr;
            }
        }

        const minEnd = new Date(resolvedStartDate + 'T00:00:00');
        minEnd.setDate(minEnd.getDate() + 1);
        const minEndStr = toDateInput(minEnd);
        if (!resolvedEndDate || resolvedEndDate < minEndStr) {
            resolvedEndDate = minEndStr;
        }

        if (new Date(resolvedStartDate) > new Date(resolvedEndDate)) {
            setMsg({ type: 'error', text: 'Start date cannot be after end date.' });
            return;
        }
        if (usageLimitType !== 'unlimited') {
            const parsed = parseInt(usageLimitValue, 10);
            if (!usageLimitValue || isNaN(parsed) || parsed < 1) {
                setMsg({ type: 'error', text: 'Usage limit must be a whole number of at least 1.' });
                return;
            }
        }

        if (status === 'active') {
            setStartDate(todayStr);
        }

        setSaving(true);
        setMsg(null);

        let finalImageUrl = imageUrl;
        if (imageFile instanceof File) {
            const { url, error: uploadErr } = await uploadToFirebaseStorage(imageFile, 'jg-printing/promotions');
            if (uploadErr) { setMsg({ type: 'error', text: `Image upload failed: ${uploadErr}` }); setSaving(false); return; }
            finalImageUrl = url;
        }

        const payload = {
            status,
            promotionName:   promotionName.trim(),
            promoCode:       promoCode.trim(),
            description:     description.trim(),
            discountedProducts,
            promotionType,
            discountValue,
            imageUrl:        finalImageUrl,
            startDate:       new Date(resolvedStartDate + 'T00:00:00'),
            endDate:         new Date(resolvedEndDate   + 'T23:59:59'),
            usageLimitType,
            usageLimitValue: usageLimitType !== 'unlimited' ? parseInt(usageLimitValue, 10) : null,
        };

        try {
            if (isNew) {
                await addDoc(collection(db, 'promotions'), { ...payload, totalUsageCount: 0, createdAt: serverTimestamp() });
                setMsg({ type: 'success', text: 'Promotion created successfully.' });
            } else {
                await updateDoc(doc(db, 'promotions', existing.id), { ...payload, updatedAt: serverTimestamp() });
                setMsg({ type: 'success', text: 'Promotion updated successfully.' });
            }
            setTimeout(() => navigate('/admin/promotions/manage'), 1200);
        } catch (err) {
            console.error('Error saving promotion:', err);
            setMsg({ type: 'error', text: 'Failed to save. Please try again.' });
        }
        setSaving(false);
    }

    const selectorTree = useMemo(() => {
        const categoriesMap = new Map();

        categories.forEach((category) => {
            const categoryName = normalizeText(category.name);
            if (!categoryName) return;

            if (!categoriesMap.has(categoryName)) {
                categoriesMap.set(categoryName, {
                    name: categoryName,
                    subcategories: new Map(),
                });
            }

            const categoryEntry = categoriesMap.get(categoryName);
            (Array.isArray(category.subcategories) ? category.subcategories : []).forEach((subcategory) => {
                const subcategoryName = normalizeText(subcategory);
                if (!subcategoryName || categoryEntry.subcategories.has(subcategoryName)) return;
                categoryEntry.subcategories.set(subcategoryName, []);
            });
        });

        products.filter(isProductInStock).forEach((product) => {
            const categoryName = normalizeText(product.category);
            const subcategoryName = normalizeText(product.subcategory || product.subCategory);
            const productName = normalizeText(product.name);

            if (!categoryName) return;

            if (!categoriesMap.has(categoryName)) {
                categoriesMap.set(categoryName, {
                    name: categoryName,
                    subcategories: new Map(),
                });
            }

            const categoryEntry = categoriesMap.get(categoryName);

            if (subcategoryName && !categoryEntry.subcategories.has(subcategoryName)) {
                categoryEntry.subcategories.set(subcategoryName, []);
            }

            if (subcategoryName && productName) {
                const productNames = categoryEntry.subcategories.get(subcategoryName) || [];
                if (!productNames.includes(productName)) {
                    productNames.push(productName);
                    productNames.sort((a, b) => a.localeCompare(b));
                    categoryEntry.subcategories.set(subcategoryName, productNames);
                }
            }
        });

        return Array.from(categoriesMap.values())
            .map((category) => ({
                name: category.name,
                subcategories: Array.from(category.subcategories.entries())
                    .map(([subcategory, productNames]) => ({
                        value: subcategory,
                        products: productNames,
                    }))
                    .sort((a, b) => a.value.localeCompare(b.value)),
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [categories, products]);

    return (
        <div className="apd-page">

            {/* ── Header ── */}
            <div className="apd-header">
                <div className="apd-header-left">
                    <button className="apd-back-btn" onClick={() => navigate('/admin/promotions/manage')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6"/>
                        </svg>
                        Back
                    </button>
                    <div>
                        <h1 className="apd-title">{isNew ? 'New Promotion' : (promotionName || 'Edit Promotion')}</h1>
                        {!isNew && <p className="apd-subtitle">ID: {existing.id}</p>}
                    </div>
                </div>
                <div className="apd-header-right">
                    {msg && (
                        <div className={`apd-msg apd-msg--${msg.type}`}>
                            {msg.type === 'success'
                                ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                                : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                            }
                            {msg.text}
                        </div>
                    )}
                    <button className="apd-save-btn" onClick={handleSave} disabled={saving}>
                        {saving ? (
                            <><span className="apd-btn-spinner"/> Saving…</>
                        ) : (
                            <>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                                    <polyline points="17 21 17 13 7 13 7 21"/>
                                    <polyline points="7 3 7 8 15 8"/>
                                </svg>
                                Save Details
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* ── Body ── */}
            <div className="apd-body">

                {/* ── LEFT column: Status, Schedule, Usage Limit ── */}
                <div className="apd-left">

                    {/* Status */}
                    <div className="apd-card">
                        <h2 className="apd-card-title">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                            Status
                        </h2>
                        <div className="apd-status-preview"><StatusBadge status={status} /></div>
                        <div className="apd-radio-group">
                            {['active', 'inactive', 'scheduled'].map(s => (
                                <label key={s} className={`apd-radio-item ${status === s ? 'apd-radio-item--active' : ''}`}>
                                    <input type="radio" name="status" value={s} checked={status === s} onChange={() => setStatus(s)} />
                                    <span className="apd-radio-dot" />
                                    <span>{s.charAt(0).toUpperCase() + s.slice(1)}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Schedule */}
                    <div className="apd-card">
                        <h2 className="apd-card-title">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                            Schedule
                        </h2>
                        <div className="apd-field">
                            <label className="apd-label">Start Date <span className="apd-req">*</span></label>
                            <input
                                type="date"
                                className="apd-input"
                                value={startDate}
                                onChange={e => setStartDate(e.target.value)}
                                min={status === 'scheduled' ? getTodayDateInput() : undefined}
                                readOnly={status === 'active'}
                            />
                            {status === 'active' && (
                                <p className="apd-field-hint">Active promotions always start today.</p>
                            )}
                            {status === 'scheduled' && (
                                <p className="apd-field-hint">Scheduled promotions start on or after today.</p>
                            )}
                        </div>
                        <div className="apd-field" style={{ marginTop: 14 }}>
                            <label className="apd-label">End Date <span className="apd-req">*</span></label>
                            <input
                                type="date"
                                className="apd-input"
                                value={endDate}
                                onChange={e => setEndDate(e.target.value)}
                                min={status === 'active' || status === 'scheduled' ? getTomorrowDateInput() : undefined}
                            />
                            {(status === 'active' || status === 'scheduled') && (
                                <p className="apd-field-hint">End date must be at least 1 day after the start date.</p>
                            )}
                        </div>
                    </div>

                    {/* Usage Limit */}
                    <div className="apd-card">
                        <h2 className="apd-card-title">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                            Usage Limit
                        </h2>
                        {!isNew && (
                            <div className="apd-usage-counter">
                                <span className="apd-usage-counter-label">Total redemptions so far</span>
                                <span className="apd-usage-counter-value">{totalUsageCount}</span>
                            </div>
                        )}
                        <div className="apd-radio-group">
                            {[
                                ['unlimited', 'Unlimited — no cap'],
                                ['total',     'Total uses cap (across all users)'],
                                ['per_user',  'Per-user cap (each user)'],
                            ].map(([value, label]) => (
                                <label key={value} className={`apd-radio-item ${usageLimitType === value ? 'apd-radio-item--active' : ''}`}>
                                    <input type="radio" name="usageLimitType" value={value} checked={usageLimitType === value}
                                        onChange={() => { setUsageLimitType(value); if (value === 'unlimited') setUsageLimitValue(''); }} />
                                    <span className="apd-radio-dot" />
                                    <span>{label}</span>
                                </label>
                            ))}
                        </div>
                        {usageLimitType !== 'unlimited' && (
                            <div className="apd-field" style={{ marginTop: 14 }}>
                                <label className="apd-label">
                                    {usageLimitType === 'total' ? 'Maximum total redemptions' : 'Maximum uses per user'}
                                    <span className="apd-req"> *</span>
                                </label>
                                <input type="number" className="apd-input" placeholder="e.g. 1" min="1" step="1"
                                    value={usageLimitValue} onChange={e => setUsageLimitValue(e.target.value)} />
                                <p className="apd-img-hint" style={{ marginTop: 6 }}>
                                    {usageLimitType === 'total'
                                        ? 'The promo will be disabled automatically after this many total uses.'
                                        : 'Each customer can only apply this code this many times.'}
                                </p>
                            </div>
                        )}
                    </div>

                </div>{/* end .apd-left */}

                {/* ── RIGHT column: Promotion Info + (Promo Image | Live Preview) ── */}
                <div className="apd-right">

                    {/* Promotion Information */}
                    <div className="apd-card">
                        <h2 className="apd-card-title">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                            Promotion Information
                        </h2>

                        <div className="apd-info-grid">

                            {/* Row 1: Name + Code */}
                            <div className="apd-info-row">
                                <div className="apd-field">
                                    <label className="apd-label">Promotion Name <span className="apd-req">*</span></label>
                                    <input type="text" className="apd-input" placeholder="e.g. October Sale"
                                        value={promotionName} onChange={e => setPromotionName(e.target.value)} />
                                </div>
                                <div className="apd-field">
                                    <label className="apd-label">Promo Code <span className="apd-req">*</span></label>
                                    <input type="text" className="apd-input apd-input--code" placeholder="e.g. SAVE20"
                                        value={promoCode} onChange={e => setPromoCode(e.target.value.toUpperCase())} />
                                </div>
                            </div>

                            {/* Row 2: Promotion Type */}
                            <div className="apd-field">
                                <label className="apd-label">Promotion Type</label>
                                <div className="apd-type-row">
                                    {[['percentage', 'Percentage ( % )'], ['fixed', 'Fixed Amount ( ₱ )']].map(([val, label]) => (
                                        <label key={val} className={`apd-type-card ${promotionType === val ? 'apd-type-card--active' : ''}`}>
                                            <input type="radio" name="promoType" value={val} checked={promotionType === val} onChange={() => setPromotionType(val)} />
                                            <span>{label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Row 3: Discounted Products + Discount Value */}
                            <div className="apd-info-row">
                                <div className="apd-field">
                                    <label className="apd-label">Discounted Products</label>
                                    <div className="apd-select-wrap" ref={dropdownRef} style={{ position: 'relative' }}>
                                        <div
                                            className="apd-input"
                                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => setDropdownOpen(o => !o)}
                                        >
                                            <span style={{ color: discountedProducts ? 'inherit' : '#aaa' }}>
                                                {discountedProducts || 'Select product / category…'}
                                            </span>
                                            <svg style={{ flexShrink: 0, transform: dropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
                                                width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <polyline points="6 9 12 15 18 9"/>
                                            </svg>
                                        </div>
                                        {dropdownOpen && (
                                            <div style={{
                                                position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                                                background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8,
                                                boxShadow: '0 4px 16px rgba(0,0,0,0.10)', zIndex: 999,
                                                maxHeight: 320, overflowY: 'auto',
                                            }}>
                                                {['', 'All Products'].map((val, i) => (
                                                    <div key={i}
                                                        onClick={() => { if (i === 0) return; setDiscountedProducts(val); setDropdownOpen(false); }}
                                                        style={{
                                                            padding: i === 0 ? '8px 14px' : '9px 14px',
                                                            color: i === 0 ? '#aaa' : discountedProducts === val ? '#fff' : '#222',
                                                            background: i === 0 ? '#fff' : discountedProducts === val ? '#1a5fa8' : '#fff',
                                                            cursor: i === 0 ? 'default' : 'pointer',
                                                            fontSize: 13,
                                                            borderBottom: i === 0 ? '1px solid #f0f0f0' : 'none',
                                                        }}
                                                        onMouseEnter={e => { if (i !== 0 && discountedProducts !== val) e.currentTarget.style.background = '#f5f8ff'; }}
                                                        onMouseLeave={e => { if (i !== 0 && discountedProducts !== val) e.currentTarget.style.background = '#fff'; }}
                                                    >
                                                        {i === 0 ? 'Select product / category…' : 'All Products'}
                                                    </div>
                                                ))}
                                                {selectorTree.map(category => (
                                                    <div key={category.name}>
                                                        <div
                                                            onClick={() => {
                                                                setDiscountedProducts(category.name);
                                                                setExpandedCats(prev => ({ ...prev, [category.name]: !prev[category.name] }));
                                                            }}
                                                            style={{
                                                                padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                                fontSize: 13, fontWeight: 700,
                                                                color: discountedProducts === category.name ? '#fff' : '#333',
                                                                background: discountedProducts === category.name ? '#1a5fa8' : '#f9f9f9',
                                                                borderTop: '1px solid #f0f0f0', cursor: 'pointer',
                                                            }}
                                                            onMouseEnter={e => { if (discountedProducts !== category.name) e.currentTarget.style.background = '#eef3fb'; }}
                                                            onMouseLeave={e => { if (discountedProducts !== category.name) e.currentTarget.style.background = '#f9f9f9'; }}
                                                        >
                                                            <span>{category.name}</span>
                                                            <svg style={{ transform: expandedCats[category.name] ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
                                                                width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                                <polyline points="6 9 12 15 18 9"/>
                                                            </svg>
                                                        </div>
                                                        {expandedCats[category.name] && category.subcategories.map(sub => (
                                                            <div key={sub.value}>
                                                                <div
                                                                    onClick={() => {
                                                                        setDiscountedProducts(sub.value);
                                                                        setExpandedSubs(prev => ({ ...prev, [sub.value]: !prev[sub.value] }));
                                                                    }}
                                                                    style={{
                                                                        padding: '8px 14px 8px 26px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                                        fontSize: 13, fontWeight: 600,
                                                                        color: discountedProducts === sub.value ? '#fff' : '#444',
                                                                        background: discountedProducts === sub.value ? '#1a5fa8' : '#fff',
                                                                        cursor: 'pointer',
                                                                    }}
                                                                    onMouseEnter={e => { if (discountedProducts !== sub.value) e.currentTarget.style.background = '#eef3fb'; }}
                                                                    onMouseLeave={e => { if (discountedProducts !== sub.value) e.currentTarget.style.background = '#fff'; }}
                                                                >
                                                                    <span>{sub.value}</span>
                                                                    {sub.products.length > 0
                                                                        ? <svg style={{ transform: expandedSubs[sub.value] ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
                                                                            width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                                            <polyline points="6 9 12 15 18 9"/>
                                                                          </svg>
                                                                        : <span style={{ fontSize: 10, color: '#bbb' }}>no products</span>
                                                                    }
                                                                </div>
                                                                {sub.products.length > 0 && expandedSubs[sub.value] && (
                                                                    sub.products.map(productName => (
                                                                            <div key={productName}
                                                                                onClick={() => { setDiscountedProducts(productName); setDropdownOpen(false); }}
                                                                                style={{
                                                                                    padding: '8px 14px 8px 40px', fontSize: 13,
                                                                                    color: discountedProducts === productName ? '#fff' : '#333',
                                                                                    background: discountedProducts === productName ? '#1a5fa8' : '#fff',
                                                                                    cursor: 'pointer',
                                                                                }}
                                                                                onMouseEnter={e => { if (discountedProducts !== productName) e.currentTarget.style.background = '#eef3fb'; }}
                                                                                onMouseLeave={e => { if (discountedProducts !== productName) e.currentTarget.style.background = '#fff'; }}
                                                                            >
                                                                                {productName}
                                                                            </div>
                                                                        ))
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="apd-field">
                                    <label className="apd-label">
                                        {promotionType === 'percentage' ? 'Discount Percentage' : 'Discount Amount'}
                                        <span className="apd-req"> *</span>
                                    </label>
                                    <div className="apd-discount-wrap">
                                        {promotionType === 'fixed' && <span className="apd-discount-affix">₱</span>}
                                        <input type="number" className="apd-input"
                                            placeholder={promotionType === 'percentage' ? 'e.g. 20' : 'e.g. 100'}
                                            value={discountValue} min="0"
                                            max={promotionType === 'percentage' ? 100 : undefined}
                                            onChange={e => setDiscountValue(e.target.value)} />
                                        {promotionType === 'percentage' && <span className="apd-discount-affix">%</span>}
                                    </div>
                                </div>
                            </div>

                            {/* Row 4: Description */}
                            <div className="apd-field">
                                <label className="apd-label">Description <span className="apd-optional">(shown on promo card)</span></label>
                                <textarea className="apd-textarea"
                                    placeholder="e.g. Get 20% OFF on all products sitewide!"
                                    value={description} onChange={e => setDescription(e.target.value)} rows={3} />
                            </div>

                        </div>
                    </div>

                    {/* ── Bottom row: Promo Image + Live Preview side by side ── */}
                    <div className="apd-bottom-row">

                        {/* Promo Image */}
                        <div className="apd-card apd-bottom-card">
                            <h2 className="apd-card-title">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                                Promo Image
                            </h2>
                            <div className="apd-upload-area" onClick={() => fileRef.current.click()}>
                                {imagePreview ? (
                                    <img src={imagePreview} alt="preview" className="apd-upload-preview" />
                                ) : (
                                    <div className="apd-upload-placeholder">
                                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                                            <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                                        </svg>
                                        <span>Click to upload</span>
                                        <span className="apd-upload-hint">PNG, JPG, WEBP</span>
                                    </div>
                                )}
                            </div>
                            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleImageChange} />
                            {imagePreview && (
                                <button className="apd-change-img-btn" onClick={() => fileRef.current.click()}>Change Image</button>
                            )}
                            <p className="apd-img-hint">Used as the background for the promo card on the Promotions page.</p>
                        </div>

                        {/* Live Preview */}
                        <div className="apd-card apd-bottom-card apd-preview-card">
                            <h2 className="apd-card-title">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                Live Preview
                            </h2>
                            <div className="apd-preview">
                                <div className="apd-preview-inner" style={{ backgroundImage: imagePreview ? `url(${imagePreview})` : 'none' }}>
                                    {!imagePreview && <div className="apd-preview-no-image">No image uploaded</div>}
                                    <div className="apd-preview-overlay">
                                        <p className="apd-preview-name">{promotionName || 'Promotion Name'}</p>
                                        <p className="apd-preview-desc">{description || 'Promo description will appear here.'}</p>
                                        {promoCode && (
                                            <div className="apd-preview-code">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                                                {promoCode}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {endDate && (
                                    <p className="apd-preview-valid">
                                        Valid until: {new Date(endDate + 'T00:00:00').toLocaleDateString('en-PH', { month: 'long', day: 'numeric', year: 'numeric' })}
                                    </p>
                                )}
                            </div>
                        </div>

                    </div>{/* end .apd-bottom-row */}

                </div>{/* end .apd-right */}
            </div>
        </div>
    );
}
// safe